import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, openModal } from '../../actions/modalActions';
import { clearCart } from '../../actions/cartActions';
import { addInvoice } from '../../actions/POS/invoiceActions';
import Modal from '../common/Modal';
import {
  FiBook,
  FiSearch,
  FiUser,
  FiAlertTriangle,
  FiCheckCircle,
  FiArrowLeft,
  FiX,
  FiUserPlus,
  FiPhone,
  FiMail,
  FiCreditCard,
  FiAlertCircle,
  FiChevronRight,
} from 'react-icons/fi';

/* ─── Mock customer data (replace with real API/Redux when ready) ─── */
const MOCK_CUSTOMERS = [
  { id: 1, name: 'Kasun Perera',     phone: '077-123-4567', email: 'kasun@example.com',  creditLimit: 50000, usedCredit: 12500 },
  { id: 2, name: 'Nimal Silva',      phone: '071-234-5678', email: 'nimal@example.com',  creditLimit: 25000, usedCredit: 24000 },
  { id: 3, name: 'Sanduni Fernando', phone: '076-345-6789', email: 'sanduni@example.com',creditLimit: 75000, usedCredit: 8000  },
  { id: 4, name: 'Ravindu Jayawardena', phone: '070-456-7890', email: 'ravindu@example.com', creditLimit: 100000, usedCredit: 55000 },
  { id: 5, name: 'Chamari Bandara',  phone: '078-567-8901', email: 'chamari@example.com',creditLimit: 30000, usedCredit: 0    },
  { id: 6, name: 'Thilina Rajapaksa',phone: '072-678-9012', email: 'thilina@example.com',creditLimit: 20000, usedCredit: 19800},
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);

/* ─────────────────────────────────────────────────────────────────────
   Add Customer Mini-Form
──────────────────────────────────────────────────────────────────────*/
function AddCustomerForm({ darkMode, onAdd, onCancel }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', creditLimit: '' });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim())        e.name        = 'Name is required';
    if (!form.phone.trim())       e.phone       = 'Phone is required';
    if (!form.creditLimit || isNaN(Number(form.creditLimit)) || Number(form.creditLimit) <= 0)
      e.creditLimit = 'Enter a valid credit limit';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onAdd({
      id: Date.now(),
      name:        form.name.trim(),
      phone:       form.phone.trim(),
      email:       form.email.trim(),
      creditLimit: Number(form.creditLimit),
      usedCredit:  0,
    });
  };

  const inputCls = `w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors ${
    darkMode
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-amber-500'
      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-amber-500'
  }`;

  const field = (label, key, type = 'text', placeholder = '') => (
    <div>
      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        className={`${inputCls} ${errors[key] ? 'border-red-400' : ''}`}
      />
      {errors[key] && <p className="text-xs text-red-500 mt-0.5">{errors[key]}</p>}
    </div>
  );

  return (
    <div className={`rounded-xl border p-4 mt-3 ${darkMode ? 'bg-gray-700/40 border-gray-600' : 'bg-amber-50 border-amber-200'}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 bg-gradient-to-br from-amber-500 to-orange-500 rounded-md">
          <FiUserPlus className="w-3.5 h-3.5 text-white" />
        </div>
        <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Add New Customer</h3>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {field('Full Name *',     'name',        'text',   'Customer name')}
        {field('Phone *',         'phone',       'tel',    '07X-XXX-XXXX')}
        {field('Email',           'email',       'email',  'email@example.com')}
        {field('Credit Limit (Rs.) *', 'creditLimit', 'number', '0.00')}
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleSubmit}
          className="flex-1 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-semibold rounded-lg transition-all"
        >
          Save Customer
        </button>
        <button
          onClick={onCancel}
          className={`flex-1 py-2 text-sm font-semibold rounded-lg border transition-all ${
            darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
          }`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Main CreditBookModal
──────────────────────────────────────────────────────────────────────*/
const CreditBookModal = () => {
  const dispatch  = useDispatch();
  const { darkMode }   = useSelector(state => state.ui);
  const { modalProps } = useSelector(state => state.ui);

  const items      = modalProps?.items      || [];
  const subtotal   = modalProps?.subtotal   || 0;
  const discount   = modalProps?.discount   || 0;
  const tax        = modalProps?.tax        || 0;
  const total      = modalProps?.total      || 0;
  const tabId      = modalProps?.tabId;

  const [search,       setSearch]       = useState('');
  const [selected,     setSelected]     = useState(null);
  const [processing,   setProcessing]   = useState(false);
  const [success,      setSuccess]      = useState(false);
  const [showAddForm,  setShowAddForm]  = useState(false);
  const [customers,    setCustomers]    = useState(MOCK_CUSTOMERS);

  /* filter */
  const filtered = useMemo(() =>
    customers.filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.phone.includes(search) ||
      (c.email && c.email.toLowerCase().includes(search.toLowerCase()))
    ), [customers, search]);

  const availableCredit = selected ? selected.creditLimit - selected.usedCredit : 0;
  const canPay          = selected && availableCredit >= total;

  const creditUsagePercent = selected
    ? Math.min(100, Math.round((selected.usedCredit / selected.creditLimit) * 100))
    : 0;

  const getCreditStatusColor = (pct) => {
    if (pct >= 90) return { bar: 'bg-red-500',    text: 'text-red-500',    badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
    if (pct >= 70) return { bar: 'bg-amber-500',  text: 'text-amber-500',  badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
    return           { bar: 'bg-green-500',   text: 'text-green-600',  badge: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
  };

  const handleBack = () => { if (!processing) dispatch(closeModal()); };

  const handleAddCustomer = (newCustomer) => {
    setCustomers(prev => [newCustomer, ...prev]);
    setSelected(newCustomer);
    setShowAddForm(false);
  };

  const handleConfirm = async () => {
    if (!canPay || processing) return;
    setProcessing(true);
    try {
      const invoiceResponse = await dispatch(addInvoice({
        items, subtotal, discount, tax, total,
        method: 'creditbook',
        splitPayment: false,
        amount: total,
        tabId,
        creditCustomer: {
          id:    selected.id,
          name:  selected.name,
          phone: selected.phone,
        },
      }));
      const invoiceData = invoiceResponse?.payload || invoiceResponse;
      setSuccess(true);

      setTimeout(() => {
        dispatch(closeModal());
        dispatch(openModal('INVOICE', {
          items, subtotal, discount, tax, total,
          paymentMethod: 'creditbook',
          splitPayment:  null,
          paidAmount:    total,
          changeAmount:  0,
          tabId,
          invoiceData,
          creditCustomer: selected,
        }));
        if (tabId) dispatch(clearCart(tabId));
      }, 900);
    } catch {
      setProcessing(false);
    }
  };

  /* ── colours & shared styles ── */
  const cardCls = `rounded-xl border ${darkMode ? 'bg-gray-700/40 border-gray-600' : 'bg-gray-50 border-gray-200'}`;

  return (
    <Modal size="lg">
      <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>

        {/* ── Header ── */}
        <div className={`p-3 border-b ${darkMode ? 'border-gray-700 bg-gray-700/50' : 'border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50'}`}>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              disabled={processing}
              className={`p-1.5 rounded-md transition-all ${processing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/50 dark:hover:bg-gray-600/50'}`}
            >
              <FiArrowLeft className="w-4 h-4 text-gray-700 dark:text-gray-300" />
            </button>
            <div className="p-1.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-md shadow">
              <FiBook className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-bold text-gray-900 dark:text-white">Credit Book Payment</h2>
              <p className="text-xs text-gray-600 dark:text-gray-400">Select a customer to add to credit book</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Due</p>
              <p className="text-base font-bold text-amber-600 dark:text-amber-400">Rs. {formatCurrency(total)}</p>
            </div>
          </div>
        </div>

        <div className="p-3 space-y-3">

          {/* ── Search bar + Add button ── */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, phone or email…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setSelected(null); }}
                className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg outline-none transition-colors ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-amber-500'
                    : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-amber-500'
                }`}
              />
            </div>
            <button
              onClick={() => { setShowAddForm(v => !v); setSelected(null); }}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-all ${
                showAddForm
                  ? 'bg-amber-500 text-white border-amber-500'
                  : darkMode
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {showAddForm ? <FiX className="w-4 h-4" /> : <FiUserPlus className="w-4 h-4" />}
              {showAddForm ? 'Cancel' : 'New'}
            </button>
          </div>

          {/* ── Add Customer Form ── */}
          {showAddForm && (
            <AddCustomerForm
              darkMode={darkMode}
              onAdd={handleAddCustomer}
              onCancel={() => setShowAddForm(false)}
            />
          )}

          {/* ── Customer List ── */}
          {!showAddForm && (
            <div className={`rounded-xl border overflow-hidden ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
              <div className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                {filtered.length} Customer{filtered.length !== 1 ? 's' : ''}
              </div>
              <div className="max-h-44 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="py-6 text-center text-gray-400 text-sm">No customers found</div>
                ) : (
                  filtered.map((c) => {
                    const pct    = Math.min(100, Math.round((c.usedCredit / c.creditLimit) * 100));
                    const avail  = c.creditLimit - c.usedCredit;
                    const enough = avail >= total;
                    const colors = getCreditStatusColor(pct);
                    const isSelected = selected?.id === c.id;

                    return (
                      <button
                        key={c.id}
                        onClick={() => { setSelected(c); setShowAddForm(false); }}
                        className={`w-full text-left px-3 py-2.5 flex items-center gap-3 border-b last:border-b-0 transition-all ${
                          darkMode ? 'border-gray-700' : 'border-gray-100'
                        } ${
                          isSelected
                            ? darkMode ? 'bg-amber-900/30' : 'bg-amber-50'
                            : darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                        }`}
                      >
                        {/* Avatar */}
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                          isSelected ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white' : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {c.name.charAt(0).toUpperCase()}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{c.name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ml-2 flex-shrink-0 ${colors.badge}`}>
                              {pct}% used
                            </span>
                          </div>
                          <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.phone}</p>
                          {/* Mini credit bar */}
                          <div className="mt-1 flex items-center gap-2">
                            <div className={`flex-1 h-1 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                              <div className={`h-1 rounded-full ${colors.bar}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className={`text-xs font-medium flex-shrink-0 ${enough ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                              Rs. {formatCurrency(avail)}
                            </span>
                          </div>
                        </div>
                        <FiChevronRight className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-amber-500' : 'text-gray-400'}`} />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* ── Selected Customer Detail Card ── */}
          {selected && !showAddForm && (
            <div className={`rounded-xl border p-3 ${
              canPay
                ? darkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'
                : darkMode ? 'bg-red-900/20 border-red-700'   : 'bg-red-50 border-red-200'
            }`}>
              {/* Customer header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold">
                    {selected.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selected.name}</p>
                    <p className={`text-xs flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <FiPhone className="w-3 h-3" /> {selected.phone}
                    </p>
                    {selected.email && (
                      <p className={`text-xs flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <FiMail className="w-3 h-3" /> {selected.email}
                      </p>
                    )}
                  </div>
                </div>
                <button onClick={() => setSelected(null)} className="p-1 rounded-md hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
                  <FiX className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              {/* Credit gauge */}
              <div className={`rounded-lg p-3 mb-3 ${darkMode ? 'bg-gray-700/50' : 'bg-white/70'}`}>
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Credit Utilisation</span>
                  <span className={`text-xs font-bold ${getCreditStatusColor(creditUsagePercent).text}`}>{creditUsagePercent}%</span>
                </div>
                <div className={`w-full h-2.5 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} overflow-hidden`}>
                  <div
                    className={`h-2.5 rounded-full transition-all ${getCreditStatusColor(creditUsagePercent).bar}`}
                    style={{ width: `${creditUsagePercent}%` }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[
                    { label: 'Credit Limit',     val: selected.creditLimit, color: 'text-gray-600 dark:text-gray-300' },
                    { label: 'Used',             val: selected.usedCredit,  color: 'text-red-500' },
                    { label: 'Available',        val: availableCredit,      color: canPay ? 'text-green-600 dark:text-green-400' : 'text-red-500' },
                  ].map(({ label, val, color }) => (
                    <div key={label} className="text-center">
                      <p className={`text-xs font-bold ${color}`}>Rs. {formatCurrency(val)}</p>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bill Summary */}
              <div className={`rounded-lg p-2.5 mb-3 ${darkMode ? 'bg-gray-700/50' : 'bg-white/70'}`}>
                <p className={`text-xs font-semibold mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Bill Summary</p>
                {[
                  { label: 'Subtotal',  val: subtotal,  cls: '' },
                  { label: 'Discount',  val: discount,  cls: 'text-red-500', prefix: '-' },
                  { label: 'Tax',       val: tax,        cls: '' },
                ].map(({ label, val, cls, prefix }) => (
                  <div key={label} className="flex justify-between text-xs mb-0.5">
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{label}:</span>
                    <span className={`font-medium ${cls}`}>{prefix}Rs. {formatCurrency(val)}</span>
                  </div>
                ))}
                <div className={`flex justify-between text-sm font-bold mt-1.5 pt-1.5 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <span className={darkMode ? 'text-white' : 'text-gray-800'}>Total to Credit:</span>
                  <span className="text-amber-600 dark:text-amber-400">Rs. {formatCurrency(total)}</span>
                </div>
              </div>

              {/* Insufficient credit warning */}
              {!canPay && (
                <div className={`flex items-start gap-2 p-2 rounded-lg mb-3 text-xs ${darkMode ? 'bg-red-900/30 text-red-300 border border-red-700' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  <FiAlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    Insufficient credit. Available: <strong>Rs. {formatCurrency(availableCredit)}</strong> — needs <strong>Rs. {formatCurrency(total - availableCredit)}</strong> more.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── Order summary (no customer selected) ── */}
          {!selected && !showAddForm && (
            <div className={`rounded-xl border p-3 ${cardCls}`}>
              {[
                { label: 'Subtotal',  val: subtotal,  cls: '' },
                { label: 'Discount',  val: discount,  cls: 'text-red-500', prefix: '-' },
                { label: 'Tax',       val: tax,        cls: '' },
              ].map(({ label, val, cls, prefix }) => (
                <div key={label} className="flex justify-between text-xs mb-1">
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{label}:</span>
                  <span className={`font-medium ${cls}`}>{prefix}Rs. {formatCurrency(val)}</span>
                </div>
              ))}
              <div className={`flex justify-between font-bold mt-1 pt-1 border-t text-sm ${darkMode ? 'border-gray-600 text-white' : 'border-gray-200 text-gray-800'}`}>
                <span>Total:</span>
                <span className="text-amber-600 dark:text-amber-400">Rs. {formatCurrency(total)}</span>
              </div>
            </div>
          )}

          {/* ── Confirm Button ── */}
          {!showAddForm && (
            <button
              onClick={handleConfirm}
              disabled={!canPay || processing || success}
              className={`w-full py-3 px-4 rounded-xl font-semibold text-white text-sm transition-all flex items-center justify-center gap-2 ${
                success
                  ? 'bg-green-600'
                  : canPay && !processing
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0'
                    : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {success ? (
                <><FiCheckCircle className="w-4 h-4" /> Payment Confirmed!</>
              ) : processing ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Processing…</>
              ) : !selected ? (
                <><FiUser className="w-4 h-4" /> Select a Customer First</>
              ) : !canPay ? (
                <><FiAlertCircle className="w-4 h-4" /> Insufficient Credit</>
              ) : (
                <><FiCreditCard className="w-4 h-4" /> Confirm Credit Book — Rs. {formatCurrency(total)}</>
              )}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CreditBookModal;
